import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const ROLES = ["super_admin", "compliance_admin", "finance_admin", "campaign_manager", "content_manager", "csr_manager", "partner_manager", "support_agent", "read_only"];
const inp = "w-full rounded-lg border border-white/10 bg-[#0B132B] px-3 py-2 text-sm text-white";

function Toggle({ on, onChange, label }) {
  return (
    <label className="flex items-center justify-between rounded-xl border border-white/10 bg-[#0B132B] px-4 py-3">
      <span className="text-sm">{label}</span>
      <button type="button" onClick={() => onChange(!on)} className={`h-6 w-11 rounded-full transition ${on ? "bg-emerald-600" : "bg-white/20"}`}>
        <span className={`block h-5 w-5 rounded-full bg-white transition ${on ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </label>
  );
}

export default function AdminSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed (needs settings permission)"); }
  };

  const setRole = async (uid, role) => {
    try { await api.put(`/admin/users/${uid}/role`, { role }); toast.success("Role updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!s) return <p className="text-emerald-100/50">Loading…</p>;
  const pp = s.payment_providers || {};

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl font-bold">Settings</h1>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#1C2541] p-6">
        <h3 className="font-semibold">Brand & Content</h3>
        <div className="mt-4 space-y-3">
          <label className="block text-sm">Tagline<input className={inp} value={s.tagline || ""} onChange={(e) => setS({ ...s, tagline: e.target.value })} data-testid="settings-tagline" /></label>
          <label className="block text-sm">Contact Email<input className={inp} value={s.email || ""} onChange={(e) => setS({ ...s, email: e.target.value })} /></label>
          <label className="block text-sm">Phone<input className={inp} value={s.phone || ""} onChange={(e) => setS({ ...s, phone: e.target.value })} /></label>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#1C2541] p-6">
        <h3 className="font-semibold">Impact Counters</h3>
        <p className="mt-1 text-xs text-emerald-100/50">When disabled, the homepage shows "Building Impact Together" instead of numbers.</p>
        <div className="mt-4 space-y-3">
          <Toggle on={s.counters_enabled} onChange={(v) => setS({ ...s, counters_enabled: v })} label="Show homepage impact counters" />
          <Toggle on={s.impact_dashboard_enabled} onChange={(v) => setS({ ...s, impact_dashboard_enabled: v })} label="Publish public impact dashboard metrics" />
          {s.counters_enabled && (s.impact_counters || []).map((c, i) => (
            <div key={i} className="flex gap-2">
              <input className={inp} value={c.label} onChange={(e) => { const a = [...s.impact_counters]; a[i] = { ...a[i], label: e.target.value }; setS({ ...s, impact_counters: a }); }} />
              <input type="number" className="w-28 rounded-lg border border-white/10 bg-[#0B132B] px-3 py-2 text-sm text-white" value={c.value} onChange={(e) => { const a = [...s.impact_counters]; a[i] = { ...a[i], value: Number(e.target.value) }; setS({ ...s, impact_counters: a }); }} />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#1C2541] p-6">
        <h3 className="font-semibold">Payment Providers</h3>
        <div className="mt-2 rounded-lg bg-gold/10 px-3 py-2 text-xs text-gold-light">PayPal status: {s.paypal_configured ? "Configured (live keys present)" : "TEST MODE — add PAYPAL_CLIENT_ID / SECRET to enable live payments"}</div>
        <div className="mt-4 space-y-3">
          <Toggle on={pp.paypal} onChange={(v) => setS({ ...s, payment_providers: { ...pp, paypal: v } })} label="PayPal" />
          <Toggle on={pp.stripe} onChange={(v) => setS({ ...s, payment_providers: { ...pp, stripe: v } })} label="Stripe (architecture-ready)" />
          <Toggle on={pp.network} onChange={(v) => setS({ ...s, payment_providers: { ...pp, network: v } })} label="Network International (architecture-ready)" />
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#1C2541] p-6">
        <h3 className="font-semibold">Zakat Methodology</h3>
        <label className="mt-4 block text-sm">Zakat Rate (e.g. 0.025 = 2.5%)<input type="number" step="0.001" className={inp} value={s.zakat_rate} onChange={(e) => setS({ ...s, zakat_rate: Number(e.target.value) })} /></label>
      </div>

      <button onClick={save} data-testid="save-settings-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-white hover:bg-gold-light"><Save className="h-4 w-4" /> Save Settings</button>

      <div className="mt-8 rounded-2xl border border-white/10 bg-[#1C2541] p-6">
        <h3 className="font-semibold">Team & Roles</h3>
        <div className="mt-4 space-y-2">
          {users.map((u) => (
            <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#0B132B] px-4 py-3">
              <div><div className="text-sm font-semibold">{u.name}</div><div className="text-xs text-emerald-100/50">{u.email}</div></div>
              {isSuper ? (
                <select defaultValue={u.role} onChange={(e) => setRole(u.user_id, e.target.value)} data-testid={`role-select-${u.email}`} className="rounded-lg border border-white/10 bg-[#1C2541] px-3 py-1.5 text-sm">
                  {ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
                </select>
              ) : <span className="rounded-full bg-gold/20 px-3 py-1 text-xs font-bold text-gold-light">{u.role?.replace("_", " ")}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
